import { Item, ItemType } from './Item.ts';
import { LevelConfig } from '../config.ts';

export interface GirlModelConfig {
  id: string;
  nameKey: string;
  url: string;
  icon?: string;
}

export const GIRL_MODELS: GirlModelConfig[] = [
  { id: 'random', nameKey: 'girlRandom', url: '', icon: '🎲' },
  { id: 'dive-white', nameKey: 'girlDiveWhite', url: './assets/sprites/girl-dive-white.png', icon: '🤿' },
  { id: 'dive-red', nameKey: 'girlDiveRed', url: './assets/sprites/girl-dive-red.png', icon: '💋' },
  { id: 'pose-back', nameKey: 'girlPoseBack', url: './assets/sprites/girl-pose-back.png', icon: '🍑' },
  { id: 'pose-kneel', nameKey: 'girlPoseKneel', url: './assets/sprites/girl-pose-kneel.png', icon: '🧎' },
  { id: 'pinup-red', nameKey: 'girlPinupRed', url: './assets/sprites/girl-pinup-1.png', icon: '🔥' },
  { id: 'pinup-pink', nameKey: 'girlPinupPink', url: './assets/sprites/girl-pinup-pink.png', icon: '💖' }
];

export class ItemFactory {
  private virtualWidth: number;
  private customImages: Map<ItemType, HTMLImageElement> = new Map();
  private girlSprites: HTMLImageElement[] = [];
  private girlSpriteMap: Map<string, HTMLImageElement> = new Map();
  private lastSpawnX: number = -1;
  private selectedGirlId: string = 'random';

  constructor(virtualWidth: number, initialSelectedGirl: string = 'random') {
    this.virtualWidth = virtualWidth;
    this.selectedGirlId = initialSelectedGirl;
    this.preloadGirlSprites();
  }

  public setSelectedGirl(girlId: string): void {
    this.selectedGirlId = girlId;
  }

  public getSelectedGirl(): string {
    return this.selectedGirlId;
  }

  private preloadGirlSprites(): void {
    for (const model of GIRL_MODELS) {
      if (!model.url) continue;
      const img = new Image();
      img.src = model.url;
      this.girlSprites.push(img);
      this.girlSpriteMap.set(model.id, img);
    }
  }

  public registerSprite(type: ItemType, src: string): void {
    const img = new Image();
    img.src = src;
    this.customImages.set(type, img);
  }

  public spawn(level: LevelConfig, totalScore: number): Item {
    const type = this.pickRandomType(level.spawnWeights);

    // X position within safe bounds with anti-overlap spacing
    const margin = 38;
    const availableWidth = this.virtualWidth - margin * 2;
    let x = margin + Math.random() * availableWidth;

    // If too close to the previous spawn, re-roll once to prevent unfair stacking
    if (this.lastSpawnX > 0 && Math.abs(x - this.lastSpawnX) < 55) {
      x = margin + Math.random() * availableWidth;
    }
    this.lastSpawnX = x;

    const y = type === 'girl' ? -55 : -35; // Start above viewport

    // Progressive speed scaling (increases by 6% every 50 points past level threshold)
    const pointsAboveMin = Math.max(0, totalScore - level.minScore);
    const speedBoost = 1.0 + Math.floor(pointsAboveMin / 50) * 0.06;
    const vy = level.baseSpeed * speedBoost * (0.92 + Math.random() * 0.16);

    let customImage: HTMLImageElement | null = null;
    let girlVariant = 0;

    if (type === 'girl') {
      if (this.selectedGirlId === 'random' || !this.girlSpriteMap.has(this.selectedGirlId)) {
        // Random selection of falling girls among all loaded sprites
        const count = this.girlSprites.length;
        const randIdx = count > 0 ? Math.floor(Math.random() * count) : 0;
        customImage = count > 0 ? (this.girlSprites[randIdx] || null) : null;
        girlVariant = randIdx;
      } else {
        customImage = this.girlSpriteMap.get(this.selectedGirlId) || null;
        const modelIdx = GIRL_MODELS.findIndex(m => m.id === this.selectedGirlId);
        girlVariant = modelIdx > 0 ? modelIdx - 1 : 0;
      }
    } else {
      customImage = this.customImages.get(type) || null;
    }

    const item = new Item(x, y, vy, type, customImage);
    item.girlVariant = girlVariant;
    return item;
  }

  private pickRandomType(weights: LevelConfig['spawnWeights']): ItemType {
    const totalWeight =
      weights.girl + weights.penis + weights.grenade + weights.monkey + weights.dog;
    const rand = Math.random() * totalWeight;

    let cumulative = 0;

    cumulative += weights.girl;
    if (rand <= cumulative) return 'girl';

    cumulative += weights.penis;
    if (rand <= cumulative) return 'penis';

    cumulative += weights.grenade;
    if (rand <= cumulative) return 'grenade';

    cumulative += weights.monkey;
    if (rand <= cumulative) return 'monkey';

    return 'dog';
  }
}
